import * as React from 'react';
import { isEmpty, isNil, throttle } from 'lodash';
import { connect } from 'react-redux';

import { FormField } from 'components/form';
import Input from 'components/inputs/input/Input';
import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';
import RichTextEditor from 'components/inputs/rich-text-editor/RichTextEditor';
import DatePickerMulti from 'components/date-picker/date-picker-multi/DatePickerMultiLazy';
import DeliverableNoMixedCampaignContent from '../deliverable-no-mixed-campaign-content/DeliverableNoMixedCampaignContent';
import DeliverableYTWarning from '../deliverable-yt-warning/DeliverableYTWarning';

import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';
import { isEnableEntertainmentVideoSelector } from 'selectors/enableEntertainmentVideo';

import { __t, applyTranslations } from 'utils/language';
import { isRequired, isValidDateRange, maxLength } from 'utils/validators';
import { hasYTWarningModalBeingShown, setYTWarningModalBeingShown } from 'modules/deliverables/utils/yt-modal';
import {
  DEFAULT_DELIVERABLE_TYPE_IN_COMPOSER,
  DELIVERABLE_TYPE_CONTENT_PRODUCTION,
  DELIVERABLE_TYPE_YOUTUBE_VIDEO,
  MAX_DELIVERABLE_DESCRIPTION_LENGTH,
  canAddDeliverableToSubcampaigns,
  getAvailableDeliverableTypesForChannels
} from 'utils/deliverables';

import type {
  Data,
  Deliverable,
  DeliverableType,
  MixedSelectDropdownItem,
  Subcampaign,
  TextEntry
} from 'types';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./deliverable-edit-common.i18n.json'));

import './deliverable-edit-common.scss';

export const MAX_NAME_LENGTH = 35;

type OwnProps = {
  campaignId: number,
  creatorId: number,
  subcampaignId: number,
  deliverable: Deliverable,
  availableChannels: Array<string>,
  offerDeliverables: Array<Deliverable>,
  onEdit: (deliverable: Partial<Deliverable>) => void
};

type StateProps = {
  subcampaignsList: Array<Subcampaign>,
  isEntertainmentVideoEnabled: boolean
};

type DispatchProps = {
  dispatchShowConfirmationModal: (
    params: {
      title: string,
      message?: string,
      confirmButtonLabel?: string,
      innerContent?: React.ReactNode,
      onConfirm: () => Promise<void>,
      onCancel?: () => Promise<void>
    }
  ) => Promise<void>,
  dispatchHideConfirmationModal: () => Promise<void>
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  deliverable: Deliverable,
  isDefaultName: boolean
};

const COMMON_MSG_KEY = 'collabary_common';

class DeliverableEditCommonForm extends React.PureComponent<Props, State> {
  throttledHandleEdit: () => void;

  constructor(props: Props) {
    super(props);

    const { deliverable } = props;
    const deliverableType = deliverable.deliverableType || DEFAULT_DELIVERABLE_TYPE_IN_COMPOSER;

    this.state = {
      deliverable: {
        ...deliverable,
        name: (deliverable && deliverable.name) || __t(`${COMMON_MSG_KEY}.types.${deliverableType}.singular`)
      },
      isDefaultName: isNil(deliverable) || isNil(deliverable.name)
        || !isNil(deliverable.deliverableType) && deliverable.name === __t(`${COMMON_MSG_KEY}.types.${deliverable.deliverableType}.singular`)
    };

    this.throttledHandleEdit = throttle(this.handleEdit, 1000);
  }

  showYTWarningModal(): Promise<void> {
    const { dispatchHideConfirmationModal, dispatchShowConfirmationModal } = this.props;

    return new Promise((resolve) => {
      dispatchShowConfirmationModal({
        title: __t('deliverable_edit_common.yt_warning_modal_header'),
        confirmButtonLabel: __t('deliverable_edit_common.no_mixed_campaigns.button'),
        innerContent: <DeliverableYTWarning />,
        onConfirm: () => {
          resolve();
          return dispatchHideConfirmationModal();
        }
      });
    });
  }

  handleTypeChanged = (value: DeliverableType): void => {
    const { deliverable, isDefaultName } = this.state;
    const defaultName = __t(`${COMMON_MSG_KEY}.types.${value}.singular`);
    const useDefaultName = isDefaultName || isEmpty(deliverable.name) || value === DELIVERABLE_TYPE_CONTENT_PRODUCTION;
    const name = useDefaultName ? defaultName : deliverable.name;

    this.setState({
      deliverable: {
        ...deliverable,
        deliverableType: value,
        name
      },
      isDefaultName: useDefaultName
    }, this.handleEdit);
  };

  handleTypeChange = async (
    updatedItems: Array<MixedSelectDropdownItem>,
    data?: Data | null,
    selectedItem?: Partial<MixedSelectDropdownItem> | null
  ): Promise<void> => {
    const {
      campaignId,
      creatorId,
      deliverable: deliverableInProps,
      subcampaignId,
      offerDeliverables,
      subcampaignsList,
      dispatchShowConfirmationModal,
      dispatchHideConfirmationModal
    } = this.props;
    const { deliverable } = this.state;

    const value: DeliverableType = selectedItem?.identifier as DeliverableType
      || DEFAULT_DELIVERABLE_TYPE_IN_COMPOSER;

    if (deliverable.deliverableType === value) {
      return;
    }

    const offerDeliverablesToCheck = offerDeliverables.filter(entity => entity.id !== deliverableInProps.id);
    const shouldShowYTWarning = value === DELIVERABLE_TYPE_YOUTUBE_VIDEO && !hasYTWarningModalBeingShown(campaignId, creatorId);

    if (shouldShowYTWarning) {
      setYTWarningModalBeingShown(campaignId, creatorId);
    }

    if (!canAddDeliverableToSubcampaigns(value, offerDeliverablesToCheck, subcampaignsList.filter(s => s.id !== subcampaignId))) {
      dispatchShowConfirmationModal({
        title: __t('deliverable_edit_common.no_mixed_campaigns.title'),
        confirmButtonLabel: __t('deliverable_edit_common.no_mixed_campaigns.button'),
        innerContent: (
          <DeliverableNoMixedCampaignContent />
        ),
        onConfirm: () => dispatchHideConfirmationModal()
      });
      return;
    }

    if (deliverableInProps.post || (deliverableInProps.postAttachments || []).length > 0) {
      dispatchShowConfirmationModal({
        title: __t('deliverable_edit_common.edit_task_type_confirmation.title'),
        message: __t('deliverable_edit_common.edit_task_type_confirmation.message'),
        onConfirm: () => {
          this.handleTypeChanged(value);
          return dispatchHideConfirmationModal();
        },
        onCancel: () => dispatchHideConfirmationModal()
      });
      return;
    }

    if (shouldShowYTWarning) {
      await this.showYTWarningModal();
    }

    this.handleTypeChanged(value);
  };

  handleNameChange = (event: React.SyntheticEvent<TextEntry>): void => {
    const { deliverable } = this.state;
    const target = event.currentTarget;
    const value = target.value || '';

    this.setState({
      deliverable: {
        ...deliverable,
        name: value
      },
      isDefaultName: deliverable.name === value
    }, this.throttledHandleEdit);
  };

  handleDateChange = (startDate?: string | null, endDate?: string | null): void => {
    const { deliverable } = this.state;

    this.setState({
      deliverable: {
        ...deliverable,
        startDate: startDate || '',
        endDate: endDate || ''
      }
    }, this.handleEdit);
  };

  handleDescriptionChange = (value: string): void => {
    const { deliverable } = this.state;

    this.setState({
      deliverable: {
        ...deliverable,
        description: value
      }
    }, this.handleEdit);
  };

  handleEdit = () => {
    const { onEdit } = this.props;
    const { deliverableType, name, startDate, endDate, description } = this.state.deliverable;

    onEdit({
      deliverableType,
      name,
      startDate,
      endDate,
      description
    });
  };

  getDeliverableTypes(): Array<DeliverableType> {
    const { availableChannels, isEntertainmentVideoEnabled } = this.props;
    return getAvailableDeliverableTypesForChannels(availableChannels || [], isEntertainmentVideoEnabled);
  }

  getDropdownItems = (): Array<MixedSelectDropdownItem> => {
    const { deliverable } = this.state;
    const channels = this.getDeliverableTypes();

    return channels.map(channel => ({
      identifier: channel,
      isDisabled: false,
      isSelected: deliverable.deliverableType === channel,
      label: __t(`${COMMON_MSG_KEY}.types.${channel}.singular`),
      value: channel
    }));
  };

  renderTypeChooser(): React.ReactNode {
    const { deliverable } = this.props;
    const dropdownItems = this.getDropdownItems();

    return (
      <div className="deliverable-edit-common__form-section">
        <FormField
          label={__t('deliverable_edit_common.label_type_of_task')}
          labelType="highlighted"
          labelSize="collapsed"
          showValidationIcon={false}
          direction="row"
          fontSize="small"
        >
          <MixedSelectDropdown
            key={`deliverable-edit-common__dropdown--${deliverable.deliverableType}`}
            name="deliverable-edit-common__dropdown"
            items={dropdownItems}
            withSelectedInList
            className="deliverable-edit-common__dropdown"
            onItemsToggle={this.handleTypeChange}
          />
        </FormField>
      </div>
    );
  }

  renderNameField(): React.ReactNode {
    const { deliverable } = this.state;

    return (
      <div className="deliverable-edit-common__form-section deliverable-edit-common__form-section--middle">
        <FormField
          label={__t('deliverable_edit_common.name_label')}
          labelType="highlighted"
          showValidationIcon={false}
          showRequiredMark
          direction="column"
          fontSize="small"
          validators={[
            isRequired(deliverable.name),
            maxLength(deliverable.name, MAX_NAME_LENGTH)
          ]}
        >
          {
            deliverable.deliverableType === DELIVERABLE_TYPE_CONTENT_PRODUCTION ?
              <span className="deliverable-task-form__field-value">
                {deliverable.name}
              </span> :
              <Input
                value={deliverable.name}
                width="responsive"
                charCount={{
                  curCount: deliverable.name.length,
                  maxChars: MAX_NAME_LENGTH
                }}
                onChange={this.handleNameChange}
                placeholder={__t('deliverable_edit_common.name_placeholder')}
              />
          }
        </FormField>
      </div>
    );
  }

  renderDatesInput(): React.ReactNode {
    const { deliverable } = this.state;

    return (
      <DatePickerMulti
        startDateName={`startDate-${deliverable.id}`}
        startDate={deliverable.startDate}
        endDateName={`endDate-${deliverable.id}`}
        endDate={deliverable.endDate}
        onChange={this.handleDateChange}
      />
    );
  }

  renderDates(): React.ReactNode {
    const { deliverable } = this.state;

    return (
      <div className="deliverable-edit-common__form-section">
        <FormField
          label={__t('deliverable_edit_common.label_dates')}
          labelType="highlighted"
          showRequiredMark
          showValidationIcon={false}
          direction="column"
          fontSize="small"
          validators={[
            isRequired(deliverable.startDate),
            isRequired(deliverable.endDate),
            isValidDateRange(deliverable.startDate, deliverable.endDate)
          ]}
        >
          <div className="deliverable-task-form__dates">
            {this.renderDatesInput()}
          </div>
        </FormField>
      </div>
    );
  }

  renderDescription(): React.ReactNode {
    const { deliverable } = this.state;
    const deliverableDescription = deliverable.description || '';

    return (
      <div className="deliverable-edit-common__form-section">
        <FormField
          label={__t('deliverable_edit_common.description_label')}
          labelType="highlighted"
          showValidationIcon={false}
          direction="column"
          fontSize="small"
          validators={[
            maxLength(deliverable.description, MAX_DELIVERABLE_DESCRIPTION_LENGTH)
          ]}
        >
          <RichTextEditor
            key={deliverableDescription}
            canEdit
            value={deliverableDescription}
            onChange={this.handleDescriptionChange}
            charCount={{
              maxChars: MAX_DELIVERABLE_DESCRIPTION_LENGTH
            }}
          />
        </FormField>
      </div>
    );
  }

  render() {
    return (
      <React.Fragment>
        {this.renderTypeChooser()}
        {this.renderNameField()}
        {this.renderDates()}
        {this.renderDescription()}
      </React.Fragment>
    );
  }
}

const mapStateToProps = ({ account, campaignSubcampaigns, runtimeConfig }): StateProps => ({
  subcampaignsList: campaignSubcampaigns.list,
  isEntertainmentVideoEnabled: isEnableEntertainmentVideoSelector({ runtimeConfig, account })
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchShowConfirmationModal: (props) => dispatch(showConfirmModal(props)),
  dispatchHideConfirmationModal: () => dispatch(hideConfirmModal())
});

export default connect(mapStateToProps, mapDispatchToProps)(DeliverableEditCommonForm);
